import jsPDF from 'jspdf';
import dayjs from 'dayjs';

interface DocumentMetadata {
  wordCount?: number;
  estimatedReadTime?: number;
  transcriptIds?: string[];
  transcriptDates?: Record<string, string>;
}

export interface ExportableDocument {
  title: string;
  content: string;
  generatedAt: string | Date;
  metadata: DocumentMetadata;
}

// Helper function to parse markdown and extract formatting
const parseMarkdownLine = (line: string) => {
  const trimmedLine = line.trim();
  
  // Headers
  if (trimmedLine.startsWith('# ')) {
    return {
      text: trimmedLine.substring(2),
      type: 'h1',
      fontSize: 18,
      font: 'bold',
      spacing: { before: 8, after: 6 }
    };
  } else if (trimmedLine.startsWith('## ')) {
    return {
      text: trimmedLine.substring(3),
      type: 'h2',
      fontSize: 16,
      font: 'bold',
      spacing: { before: 6, after: 4 }
    };
  } else if (trimmedLine.startsWith('### ')) {
    return {
      text: trimmedLine.substring(4),
      type: 'h3',
      fontSize: 14,
      font: 'bold',
      spacing: { before: 4, after: 3 }
    };
  } else if (trimmedLine.startsWith('#### ')) {
    return {
      text: trimmedLine.substring(5),
      type: 'h4',
      fontSize: 12,
      font: 'bold',
      spacing: { before: 3, after: 2 }
    };
  }
  
  // Lists
  else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
    return {
      text: '• ' + trimmedLine.substring(2),
      type: 'ul',
      fontSize: 11,
      font: 'normal',
      indent: 10,
      spacing: { before: 1, after: 1 }
    };
  } else if (trimmedLine.match(/^\d+\.\s/)) {
    return {
      text: trimmedLine,
      type: 'ol',
      fontSize: 11,
      font: 'normal',
      indent: 10,
      spacing: { before: 1, after: 1 }
    };
  }
  
  // Blockquotes
  else if (trimmedLine.startsWith('> ')) {
    return {
      text: trimmedLine.substring(2),
      type: 'blockquote',
      fontSize: 11,
      font: 'italic',
      indent: 15,
      spacing: { before: 3, after: 3 }
    };
  }
  
  // Code blocks (simple detection)
  else if (trimmedLine.startsWith('```') || trimmedLine.startsWith('`')) {
    return {
      text: trimmedLine.replace(/`/g, ''),
      type: 'code',
      fontSize: 10,
      font: 'normal',
      indent: 5,
      spacing: { before: 2, after: 2 }
    };
  }
  
  // Horizontal rules
  else if (trimmedLine === '---' || trimmedLine === '***') {
    return {
      text: '',
      type: 'hr',
      fontSize: 11,
      font: 'normal',
      spacing: { before: 5, after: 5 }
    };
  }
  
  // Regular paragraphs
  else if (trimmedLine) {
    return {
      text: trimmedLine,
      type: 'p',
      fontSize: 11,
      font: 'normal',
      spacing: { before: 0, after: 4 }
    };
  }
  
  // Empty lines
  else {
    return {
      text: '',
      type: 'empty',
      fontSize: 11,
      font: 'normal',
      spacing: { before: 0, after: 3 }
    };
  }
};

// Helper function to process inline markdown formatting
const processInlineFormatting = (text: string): { text: string; formatting: Array<{start: number; end: number; type: string}> } => {
  const formatting: Array<{start: number; end: number; type: string}> = [];
  let processedText = text;
  
  // Bold text (**text** or __text__)
  const boldRegex = /(\*\*|__)(.*?)\1/g;
  let match;
  while ((match = boldRegex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[2].length;
    formatting.push({ start, end, type: 'bold' });
    processedText = processedText.replace(match[0], match[2]);
  }
  
  // Italic text (*text* or _text_)
  const italicRegex = /(\*|_)(.*?)\1/g;
  while ((match = italicRegex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[2].length;
    formatting.push({ start, end, type: 'italic' });
    processedText = processedText.replace(match[0], match[2]);
  }
  
  // Inline code (`text`)
  const codeRegex = /`([^`]+)`/g;
  while ((match = codeRegex.exec(text)) !== null) {
    const start = match.index;
    const end = start + match[1].length;
    formatting.push({ start, end, type: 'code' });
    processedText = processedText.replace(match[0], match[1]);
  }
  
  return { text: processedText, formatting };
};

export const exportToPDF = (document: ExportableDocument) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  let currentY = margin;
  const maxWidth = pageWidth - (margin * 2);

  // Add title and metadata
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(document.title, margin, currentY);
  currentY += 12;

  // Add a subtle line under the title
  doc.setLineWidth(0.5);
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, currentY, pageWidth - margin, currentY);
  currentY += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated: ${dayjs(document.generatedAt).format('MMMM D, YYYY h:mm A')}`, margin, currentY);
  currentY += 6;
  
  if (document.metadata.wordCount) {
    doc.text(`Word count: ${document.metadata.wordCount} words`, margin, currentY);
    currentY += 6;
  }
  
  if (document.metadata.estimatedReadTime) {
    doc.text(`Estimated read time: ${document.metadata.estimatedReadTime} minutes`, margin, currentY);
    currentY += 6;
  }
  
  currentY += 10;
  doc.setTextColor(0, 0, 0); // Reset to black

  // Process the markdown content
  const lines = document.content.split('\n');
  let inCodeBlock = false;
  
  lines.forEach((line, index) => {
    // Handle code blocks
    if (line.trim().startsWith('```')) {
      inCodeBlock = !inCodeBlock;
      if (!inCodeBlock) {
        currentY += 5; // Add space after code block
      }
      return;
    }
    
    if (inCodeBlock) {
      // Handle code block content
      doc.setFontSize(9);
      doc.setFont('courier', 'normal');
      doc.setFillColor(245, 245, 245);
      
      const codeText = line || ' '; // Ensure empty lines in code blocks are visible
      const splitText = doc.splitTextToSize(codeText, maxWidth - 10);
      
      // Check if we need a new page
      if (currentY + (splitText.length * 6) > pageHeight - margin) {
        doc.addPage();
        currentY = margin;
      }
      
      // Add background for code
      const textHeight = splitText.length * 6;
      doc.rect(margin + 5, currentY - 4, maxWidth - 10, textHeight + 2, 'F');
      
      splitText.forEach((textLine: string) => {
        doc.text(textLine, margin + 8, currentY);
        currentY += 6;
      });
      
      return;
    }
    
    const parsed = parseMarkdownLine(line);
    
    if (parsed.type === 'empty') {
      currentY += parsed.spacing.after;
      return;
    }
    
    if (parsed.type === 'hr') {
      currentY += parsed.spacing.before;
      doc.setLineWidth(0.5);
      doc.setDrawColor(150, 150, 150);
      doc.line(margin, currentY, pageWidth - margin, currentY);
      currentY += parsed.spacing.after;
      return;
    }
    
    if (!parsed.text) return;
    
    // Add spacing before
    currentY += parsed.spacing.before;
    
    // Set font properties
    doc.setFontSize(parsed.fontSize);
    doc.setFont('helvetica', parsed.font);
    
    // Process inline formatting
    const { text: processedText, formatting } = processInlineFormatting(parsed.text);
    
    // Calculate indent
    const indent = parsed.indent || 0;
    
    // Split text to fit width
    const splitText = doc.splitTextToSize(processedText, maxWidth - indent);
    
    // Check if we need a new page
    const lineHeight = parsed.fontSize * 0.6;
    if (currentY + (splitText.length * lineHeight) > pageHeight - margin) {
      doc.addPage();
      currentY = margin;
    }
    
    // Add each line of wrapped text
    splitText.forEach((textLine: string) => {
      // Special styling for blockquotes
      if (parsed.type === 'blockquote') {
        doc.setDrawColor(180, 180, 180);
        doc.setLineWidth(2);
        doc.line(margin + indent - 5, currentY - 3, margin + indent - 5, currentY + 3);
      }
      
      doc.text(textLine, margin + indent, currentY);
      currentY += lineHeight;
    });
    
    // Add spacing after
    currentY += parsed.spacing.after;
  });

  // Generate filename and save
  const filename = `${document.title.toLowerCase().replace(/\s+/g, '_')}_${dayjs(document.generatedAt).format('YYYY-MM-DD')}.pdf`;
  doc.save(filename);
}; 